import moment from 'moment';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {LEAF_TYPE_TO_VALIDATOR} from 'data-types/util/isValidBenchlingTypeValue';
import {FieldEditor} from 'pipelines/generalized-step-config-editor/editors/StepConfigEditorRouter';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import FieldError from 'ui-library/FieldError';
import {USER_EDIT_DATETIME_FORMAT} from 'util/DateConstants';
import {JSONValue} from 'util/Types';

export type PrimitiveValue = string | number | boolean | null;

export type SupportedLeafType =
  | LeafType.TEXT
  | LeafType.INTEGER
  | LeafType.DECIMAL
  | LeafType.BOOLEAN
  | LeafType.DATE
  | LeafType.DATETIME;

export interface PrimitiveValueEditorProps {
  leafType: SupportedLeafType;
  value: PrimitiveValue;
  onChange: (value: PrimitiveValue) => void;
  disabled?: boolean;
}

const CONFIG_KEY = '_v';
const CONFIG_SHAPE_PATH = [CONFIG_KEY];

function leafTypeToPrimitiveSpec(leafType: SupportedLeafType): PipelineConfigJsonSpec {
  switch (leafType) {
    case LeafType.INTEGER:
      return {kind: 'integer', required: false};
    case LeafType.DECIMAL:
      return {kind: 'number', required: false};
    case LeafType.BOOLEAN:
      return {kind: 'boolean', required: false};
    case LeafType.DATE:
      return {kind: 'date', required: false};
    case LeafType.DATETIME:
      // PipelineConfigJsonSpec has no datetime kind; validated separately in isLeafValueValid.
      return {kind: 'string', required: false};
    default:
      return {kind: 'string', required: false};
  }
}

function isLeafValueValid(value: PrimitiveValue, leafType: SupportedLeafType): boolean {
  if (value == null || value === '') {
    return true;
  }
  if (leafType === LeafType.DATETIME) {
    return typeof value === 'string' && moment(value, USER_EDIT_DATETIME_FORMAT, true).isValid();
  }
  const validator = LEAF_TYPE_TO_VALIDATOR[leafType];
  assert(validator != null, 'PrimitiveValueEditor does not support this leaf type', {leafType});
  return validator(value as JSONValue);
}

function getFormatHint(leafType: SupportedLeafType): string | undefined {
  if (leafType === LeafType.DATETIME) {
    return `Expected format: ${USER_EDIT_DATETIME_FORMAT}`;
  }
  return undefined;
}

/**
 * Primitive value editor with inline validation. Invalid intermediate values are kept in
 * local state and not propagated to onChange.
 *
 * FieldEditor requires a root `shape` spec, so we wrap the primitive spec in a single-field
 * shape keyed by `_v`.
 */
export default function PrimitiveValueEditor({
  leafType,
  value,
  onChange,
  disabled,
}: PrimitiveValueEditorProps): JSX.Element {
  const [localValue, setLocalValue] = useState<PrimitiveValue>(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const isValid = isLeafValueValid(localValue, leafType);

  const primitiveSpec = leafTypeToPrimitiveSpec(leafType);
  const configShape: PipelineConfigJsonSpec = {
    kind: 'shape',
    required: false,
    fields: {[CONFIG_KEY]: primitiveSpec},
  };
  const config = {[CONFIG_KEY]: localValue as JSONValue};

  return (
    <>
      <FieldEditor
        dataFramesInfos={{}}
        configShape={configShape}
        config={config}
        uiSchema={{configShapePath: CONFIG_SHAPE_PATH}}
        onChangeConfig={(_config, _path, newValue) => {
          const typed = newValue as PrimitiveValue;
          setLocalValue(typed);
          if (isLeafValueValid(typed, leafType)) {
            onChange(typed === '' ? null : typed);
          }
        }}
        disabled={disabled}
      />
      {!isValid && <FieldError message={getFormatHint(leafType) ?? 'Invalid value for this type'} />}
    </>
  );
}
